import {
  SalesChannelName,
  ValidationState
} from 'generated/graphql';

export const routeDirectory = {

  root: {
    raw: '/',
    path: () => '/'
  },

  accessDenied: {
    raw: '/accessDenied',
    path: () => '/accessDenied'
  },

  noStores: {
    raw: '/noStores',
    path: () => '/noStores'
  },

  auth: {
    logIn: {
      raw: '/auth',
      path: () => '/auth'
    },

    register: {
      raw: '/auth/register/:inviteCode'
    },

    forgotPassword: {
      raw: '/auth/forgotPassword',
      path: () => '/auth/forgotPassword'
    }
  },

  partner: {
    stores: {
      raw: '/partner/stores',
      path: () => '/partner/stores'
    },

    storeDetail: {
      raw: '/partner/stores/:storeId',
      path: (storeId: string, orderId?: string) =>
        `/partner/stores/${storeId}${orderId ? `?orderId=${orderId}` : ''}`
    },

    orderList: {
      raw: '/partner/stores/:storeId/orders',
      path: (storeId: string) =>
        `/partner/stores/${storeId}/orders`
    },

    storeSettings: {
      raw: '/partner/stores/:storeId/settings',
      path: (storeId: string, tabId: string) =>
        `/partner/stores/${storeId}/settings?tabId=${tabId}`
    },

    inventory: {
      raw: '/partner/stores/inventory/:tabId',
      path: (tabId: 'items' | 'templates', storeId: string) => `/partner/stores/inventory/${tabId}?storeId=${storeId}`
    }
  },

  admin: {
    root: {
      raw: '/admin',
      path: () => '/admin'
    },

    appleCareAgreements: {
      raw: '/admin/appleCareAgreements',
      path: () => '/admin/appleCareAgreements'
    },

    appSettings: {
      raw: '/admin/appSettings',
      path: () => `/admin/appSettings`
    },

    customers: {
      raw: '/admin/customers',
      path: (customerId?: string) => `/admin/customers${customerId ? `?customerId=${customerId}` : ''}`
    },

    databaseReport: {
      raw: '/admin/databaseReport/:id',
      path: (id: string) => `/admin/databaseReport/${id}`
    },

    dataCache: {
      raw: '/admin/dataCache/:validationState',
      path: (validationState: ValidationState, storeId?: string | null) => {
        const params = new URLSearchParams({});

        if (storeId) {
          params.set('storeId', storeId);
        }
        const searchParams = params.toString();

        return `/admin/dataCache/${validationState}?${searchParams}`;
      }
    },

    netSuite: {
      raw: '/admin/netSuite/:tabId',
      path: (tabId: 'pendingWrites' | 'validationErrors' | 'soap', writeRequestId?: string) =>
        `/admin/netSuite/${tabId}${writeRequestId ? `?writeRequestId=${writeRequestId}` : ''}`
    },

    orderManagement: {
      raw: '/admin/orderManagement/:tabId',
      path: (tabId: 'pendingWrites' | 'validationErrors' | 'valid' | 'invalid' | 'invalidArchived', writeRequestId?: string, storeId?: string | null) => {
        const params = new URLSearchParams();
        if (writeRequestId) {
          params.set('writeRequestId', writeRequestId);
        }
        if (storeId) {
          params.set('storeId', storeId);
        }
        const searchParams = params.toString();
        return `/admin/orderManagement/${tabId}${searchParams ? `?${searchParams}` : ''}`;
      }
    },

    validateOrder: {
      raw: '/admin/validateOrder/:storeId/order/:externalOrderId',
      path: (externalOrderId: string, storeId?: string | null) => {
        if (storeId) {
          return `/admin/validateOrder/${storeId}/order/${externalOrderId}`;
        } else {
          return `/admin/validateOrder/placeholder/order/${externalOrderId}`;
        }
      }
    },

    inventory: {
      raw: '/admin/inventory/:tabId',
      path: (tabId: 'items' | 'templates', itemId?: string) => `/admin/inventory/${tabId}${itemId ? `?itemId=${itemId}` : ''}`
    },

    orders: {
      raw: '/admin/orders/:salesChannel/:tabId',
      path: (salesChannel: SalesChannelName, tabId: string, storeId?: string) =>
        `/admin/orders/${salesChannel}/${tabId}${storeId ? `?storeId=${storeId}` : ''}`
    },

    orderDetail: {
      raw: '/admin/stores/:storeId/orders/:orderId',
      path: (storeId: string, orderId: string) =>
        `/admin/stores/${storeId}/orders/${orderId}`
    },

    reconciliationReport: {
      raw: '/admin/reconciliationReport/:salesChannel',
      path: (salesChannel: SalesChannelName) => `/admin/reconciliationReport/${salesChannel}`
    },

    returnAuthorizations: {
      list: {
        raw: '/admin/returnAuthorizations/:tabId',
        path: (
          tabId: string,
          storeId?: string
        ) => `/admin/returnAuthorizations/${tabId}${storeId ? `?storeId=${storeId}` : ''}`
      },
      detail: {
        raw: '/admin/returnAuthorizations/:returnAuthorizationId/detail',
        path: (returnAuthorizationId: string) => `/admin/returnAuthorizations/${returnAuthorizationId}/detail`
      }
    },

    returnAuthorizations_deprecated: {
      raw: '/admin/returnAuthorizations_deprecated',
      path: () => '/admin/returnAuthorizations_deprecated'
    },

    storeDetail: {
      raw: '/admin/stores/:storeId',
      path: (storeId: string, tabId: 'erpSettings' | 'brand' | 'importSettings') =>
        `/admin/stores/${storeId}?tabId=${tabId}`
    },

    thirdPartyAccounts: {
      raw: '/admin/thirdPartyAccounts',
      path: () => '/admin/thirdPartyAccounts'
    },

    thirdPartyLogistics: {
      raw: '/admin/thirdPartyLogistics',
      path: () => '/admin/thirdPartyLogistics'
    },

    debug: {
      raw: '/admin/debug',
      path: () => '/admin/debug'
    },

    tier1ReturnAuthorizations: {
      all: {
        raw: '/admin/tier1/returnAuthorizations',
        path: () => '/admin/tier1/returnAuthorizations'
      },
      list: {
        raw: '/admin/tier1/returnAuthorizations/:tabId',
        path: (tabId: string, storeId?: string, searchText?: string) => {
          const params = new URLSearchParams({});

          if (storeId) {
            params.set('storeId', storeId);
          }

          if (searchText) {
            params.set('search', searchText);
          }

          const searchParams = params.toString();
          return `/admin/tier1/returnAuthorizations/${tabId}?${searchParams}`;
        }
      },
      detail: {
        raw: '/admin/tier1/returnAuthorizations/:returnAuthorizationId/detail',
        path: (returnAuthorizationId: string) => `/admin/tier1/returnAuthorizations/${returnAuthorizationId}/detail`
      }
    }
  },

  customer: {
    orderLookup: {
      raw: '/customer/order-lookup',
      path: () => '/customer/order-lookup'
    },

    storeReferral: {
      raw: '/customer/store-referral/:storeId',
      path: (storeId: string) => `/customer/store-referral/${storeId}`
    },

    orderDetail: {
      raw: '/customer/order/:orderId',
      path: (orderId: string, storeId: string) => `/customer/order/${orderId}?storeId=${storeId}`
    }
  }

};
